import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CreditCardIcon, ExternalLinkIcon, ReceiptTextIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useSubscriptions } from "@/hooks/query/billing";
import { subscription } from "@/lib/auth-client";
import { formatDate } from "@/lib/format";
import { noIndexSeo } from "@/lib/seo";
import { getPlanByName } from "@/lib/subscriptions/subscriptions";
import * as m from "@/paraglide/messages";

export const Route = createFileRoute("/(app)/settings/billing")({
	component: RouteComponent,
	head: ({ match }) =>
		noIndexSeo({
			title: `${m.billing_title()} | StudyOS`,
			description: m.billing_description(),
			pathname: match.pathname,
		}),
});

function getStatusLabels(): Record<string, string> {
	return {
		active: m.status_active(),
		trialing: m.status_trialing(),
		canceled: m.status_canceled(),
		incomplete: m.status_incomplete(),
		past_due: m.status_pastDue(),
		unpaid: m.status_unpaid(),
		paused: m.status_paused(),
	};
}

const statusVariants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
	active: "default",
	trialing: "secondary",
	canceled: "destructive",
	incomplete: "outline",
	past_due: "destructive",
	unpaid: "destructive",
	paused: "outline",
};

function RouteComponent() {
	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium">{m.billing_title()}</h3>
				<p className="text-sm text-muted-foreground">{m.billing_description()}</p>
			</div>
			<Separator />
			<BillingPortalCard />
			<SubscriptionHistory />
		</div>
	);
}

function BillingPortalCard() {
	const { mutate: openBillingPortal, isPending } = useMutation({
		mutationKey: ["subscription", "billing-portal"],
		mutationFn: async () => {
			const result = await subscription.billingPortal({
				returnUrl: "/settings/billing",
			});
			if (result.error) {
				throw result.error;
			}
			return result;
		},
		onSuccess: (result) => {
			window.location.href = result.data.url;
		},
		meta: {
			error: { message: m.billing_openPortalFailed() },
		},
	});

	return (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle className="text-base">{m.billing_paymentInvoices()}</CardTitle>
			</CardHeader>
			<CardContent className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">{m.billing_stripeDescription()}</p>
				<Button
					variant="outline"
					size="sm"
					className="shrink-0 ml-4"
					disabled={isPending}
					onClick={() => openBillingPortal()}
				>
					<ExternalLinkIcon className="size-4 mr-1.5" />
					{m.billing_openPortal()}
				</Button>
			</CardContent>
		</Card>
	);
}

function SubscriptionHistory() {
	const { data: subscriptions, isPending } = useSubscriptions();

	const statusLabels = getStatusLabels();

	if (isPending) {
		return (
			<div className="space-y-4">
				<h4 className="text-base font-medium">{m.billing_subscriptionHistory()}</h4>
				<Skeleton className="h-48 w-full rounded-lg" />
			</div>
		);
	}

	if (!subscriptions || subscriptions.length === 0) {
		return (
			<div className="space-y-4">
				<h4 className="text-base font-medium">{m.billing_subscriptionHistory()}</h4>
				<Card>
					<CardContent className="flex flex-col items-center gap-2 py-8">
						<ReceiptTextIcon className="size-8 text-muted-foreground" />
						<p className="text-sm text-muted-foreground">
							{m.billing_noSubscriptions()}
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<h4 className="text-base font-medium">{m.billing_subscriptionHistory()}</h4>
			<Card>
				<CardContent className="p-0">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{m.billing_plan()}</TableHead>
								<TableHead>{m.billing_status()}</TableHead>
								<TableHead>{m.billing_startDate()}</TableHead>
								<TableHead>{m.billing_endDate()}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{subscriptions.map((sub) => {
								const plan = getPlanByName(sub.plan);
								return (
									<TableRow key={sub.id}>
										<TableCell>
											<div className="flex items-center gap-2">
												<CreditCardIcon className="size-4 text-muted-foreground" />
												<span className="text-sm font-medium">
													{plan?.label ?? sub.plan}
												</span>
											</div>
										</TableCell>
										<TableCell>
											<Badge
												variant={
													statusVariants[sub.status ?? "incomplete"] ??
													"outline"
												}
											>
												{statusLabels[sub.status ?? "incomplete"] ??
													sub.status}
											</Badge>
										</TableCell>
										<TableCell className="text-sm">
											{formatDate(sub.periodStart)}
										</TableCell>
										<TableCell className="text-sm">
											{formatDate(sub.periodEnd)}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		</div>
	);
}
